import { forwardRef, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { ValuationSourceIndicator } from "./SummaryTable";
import useFormatters from "./useFormaters";
import { StyledTable } from "./SummaryTable";
import { flattenInventoryRows } from "./inventoryPagination";

const InventoryPartialLine = ({ portfolio, previous_portfolio }) => {
  const { formatNumber, formatMultiple } = useFormatters();

  return (
    <>
      <td className="">{formatNumber(portfolio.quantity_aggregated)}</td>
      <td className="">{formatNumber(portfolio.remaining_invested_cost)}</td>
      <td className="">{formatNumber(portfolio.interest_in_euro)}</td>
      <td className="">{formatNumber(portfolio.capital_gain_in_euro)}</td>
      <td className="">{formatNumber(portfolio.value_in_euro)}</td>
      <td className="">
        {formatMultiple(portfolio.ongoing_valuation_multiple)}
        <ValuationSourceIndicator portfolio={portfolio} />
      </td>
    </>
  );
};

const InventoryRow = ({ row }) => {
  const { t } = useTranslation();
  const { formatDate, formatPercentage } = useFormatters();

  switch (row.type) {
    case "deal":
      return (
        <tr className="deal-row">
          <td className="text-start">
            {row.portfolio.overriden_active_deal_name}
          </td>
          <td className="text-center">
            {formatDate(row.portfolio.initial_investment_date)}
          </td>
          <td className="text-start">{t(row.asset_type.label)}</td>
          <td className="">
            {row.asset_type.value === "SHARE"
              ? formatPercentage(row.portfolio.ownership_fd)
              : ""}
          </td>
          <InventoryPartialLine
            portfolio={row.portfolio}
            previous_portfolio={row.previous_portfolio}
          />
        </tr>
      );
    case "roundTypeSubtotal":
      return (
        <tr className="deal-status-row">
          <td className="text-start" colSpan={4}>
            {t("TotalTransactionsRoundType", {
              roundType: row.round_type.label,
            })}
          </td>
          <InventoryPartialLine
            portfolio={row.portfolio}
            previous_portfolio={row.previous_portfolio}
          />
        </tr>
      );
    case "fund":
      return (
        <tr className="fund-row">
          <td className="text-start" colSpan={4}>
            {row.fund.name}
          </td>
          <InventoryPartialLine
            portfolio={row.portfolio}
            previous_portfolio={row.previous_portfolio}
          />
        </tr>
      );
    case "spacerBottom":
      return (
        <tr className="spacer-row-bottom">
          <td>&nbsp;</td>
        </tr>
      );
    case "spacer":
    default:
      return (
        <tr className="spacer-row">
          <td>&nbsp;</td>
        </tr>
      );
  }
};

const InventoryTable = forwardRef(({ vizInfo, rows }, ref) => {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  if (!vizInfo) {
    return <div>No data</div>;
  }

  const displayedRows = rows ?? flattenInventoryRows(vizInfo);

  return (
    <>
      <StyledTable ref={ref}>
        <colgroup>
          <col className="col-first" />
          <col className="col-date" />
          <col className="col-large" />
          <col className="col-large" />
          <col className="col-large" />
          <col className="col-large" />
          <col className="col-large" />
          <col className="col-large" />
          <col className="col-large" />
          <col className="col-large" />
        </colgroup>
        <thead>
          <tr>
            <th colSpan={3} className="hide-span">
              &nbsp;
            </th>
            <th colSpan={7} className="title visible-span">
              {formatDate(vizInfo.valuation_period.date)}
            </th>
          </tr>
          <tr>
            <th className="title text-start">{t("Deal")}</th>
            <th className="title">{t("Date of initial investment")}</th>
            <th className="title" title="">
              {t("Instrument type")}
            </th>
            <th className="title" title="">
              {t("% Ownership (FD)")}
            </th>
            <th
              className="title"
              title="capital_gain_dividend_and_interest_in_euro"
            >
              {t("# shares/bonds")}
            </th>
            <th className="title" title="remaining_invested_cost">
              {t("Current cost of investment")} (€)
            </th>
            <th className="title" title="interest_in_euro">
              {t("Accrued interest")} (€)
            </th>
            <th className="title" title="capital_gain_in_euro">
              {t("Capital gains and losses")} (€)
            </th>
            <th className="title" title="value_in_euro">
              {t("Fair market value")} (€)
            </th>
            <th className="title" title="ongoing_valuation_multiple">
              {t("Gross multiple")}
            </th>
          </tr>
          <tr className="spacer-row">
            <td>&nbsp;</td>
          </tr>
        </thead>
        <tbody>
          {displayedRows.map((row, index) => (
            <InventoryRow row={row} key={index} />
          ))}
        </tbody>
      </StyledTable>
    </>
  );
});

export default InventoryTable;
